import type {
  FacilityDetail,
  FacilityReviewStatus,
  FacilityUploadResult,
  PlantNameChange,
  PlantNameSource,
} from "./types";

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export interface ExportOutcome {
  exported: number;
  skipped: number;
  fileName: string;
}

const FILE_NAME_PATTERN = /filename="([^"]+)"/;

export async function setReview(
  apiFetch: ApiFetch,
  registryId: string,
  status: FacilityReviewStatus,
  note?: string,
  groupIds?: readonly number[],
  rename?: PlantNameChange,
): Promise<FacilityDetail> {
  const response = await apiFetch(`facilities/${encodeURIComponent(registryId)}/review`, {
    method: "PATCH",
    body: JSON.stringify({
      status,
      note: note?.trim() || null,
      groupIds,
      plantName: rename?.name,
      plantNameSource: rename?.source,
    }),
  });
  return response.json();
}

export async function setFacilityName(
  apiFetch: ApiFetch,
  registryId: string,
  name: string,
  source: PlantNameSource,
): Promise<FacilityDetail> {
  const response = await apiFetch(`facilities/${encodeURIComponent(registryId)}/name`, {
    method: "PUT",
    body: JSON.stringify({ name, source }),
  });
  return response.json();
}

export async function uploadFacilities(apiFetch: ApiFetch, file: File, dryRun: boolean): Promise<FacilityUploadResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await apiFetch(`facilities/upload?dryRun=${dryRun}`, { method: "POST", body: form });
  return response.json();
}

export async function exportVerified(apiFetch: ApiFetch): Promise<ExportOutcome> {
  const response = await apiFetch("facilities/export");
  const blob = await response.blob();
  const fileName =
    FILE_NAME_PATTERN.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? "verified-facilities.csv";
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return {
    exported: Number(response.headers.get("X-Exported-Count") ?? 0),
    skipped: Number(response.headers.get("X-Skipped-Without-Coordinates") ?? 0),
    fileName,
  };
}
